/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2021, 2025
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/
import { defineMessages } from 'react-intl';

export const PhoneNew = defineMessages({
  titleOne: {
    id: 'phone.PhoneNew.titleOne',
    defaultMessage: 'Your customers are calling'
  },
  titleTwo: {
    id: 'phone.PhoneNew.titleTwo',
    defaultMessage: 'Your assistant can answer'
  },
  subtitleOne: {
    id: 'phone.PhoneNew.subtitleOne',
    defaultMessage:
      'Thanks to IBM Watson speech services, your assistant is ready to take phone calls. Provide world class support over the phone by connecting your assistant with the phone integration.'
  },
  pricingNote: {
    id: 'phone.PhoneNew.pricingNote',
    defaultMessage:
      'Note: Additional charges may apply for Monthly Active Users calling in to the phone integration, charges may vary per plan.'
  },
  instructions: {
    id: 'phone.PhoneNew.instructions',
    defaultMessage: 'What you can do:'
  },
  instructionsDescription: {
    id: 'phone.PhoneNew.instructionsDescription',
    defaultMessage:
      "Select Create to get all the details, but here's what to expect:"
  },
  instructionsItemTwo: {
    id: 'phone.PhoneNew.instructionsItem2',
    defaultMessage:
      'Use a pre-existing phone number with an external provider and connect over a SIP trunk'
  },
  instructionsItemThree: {
    id: 'phone.PhoneNew.instructionsItem3',
    defaultMessage: 'Learn how to connect your assistant to your live agents'
  },
  instructionsItemFour: {
    id: 'phone.PhoneNew.instructionsItem4',
    defaultMessage:
      'Learn how to pass context to your agents to streamline the escalation process'
  },
  instructionsItemFive: {
    id: 'phone.PhoneNew.instructionsItem5',
    defaultMessage:
      'Need expert advice? <link>Contact us</link> to get in touch'
  },
  instructionsLinkByoSipTrunk: {
    id: 'phone.PhoneNew.instructionsLinkByoSipTrunk',
    defaultMessage:
      'IntelePeer is supported as the default provider. Other providers are supported by <link>bringing your own SIP trunk</link>.'
  },
  instructionsLinkLearnMore: {
    id: 'phone.PhoneNew.instructionsLinkLearnMore',
    defaultMessage: '<link>Learn more</link> about the phone integration'
  }
});

export const PhoneQuickSetup = defineMessages({
  titleOne: {
    id: 'phone.PhoneQuickSetup.title1',
    defaultMessage: 'Set up a phone connection'
  },
  description: {
    id: 'phone.PhoneQuickSetup.description',
    defaultMessage:
      'Give your system a voice to help customers over the phone. <link>Learn more</link>'
  },
  externalProviderTitle: {
    id: 'phone.PhoneQuickSetup.externalProviderTitle',
    defaultMessage: 'Use an existing phone number with an external provider'
  },
  externalProviderDescription: {
    id: 'phone.PhoneQuickSetup.externalProviderDescription',
    defaultMessage:
      'We can walk you through connecting your existing systems to Watson via a SIP trunk.'
  },
  gatewayOnPremTitle: {
    id: 'phone.PhoneQuickSetup.gatewayOnPremTitle',
    defaultMessage: 'Deploy Phone Gateway on premises'
  },
  gatewayOnPremDescription: {
    id: 'phone.PhoneQuickSetup.gatewayOnPremDescription',
    defaultMessage:
      'We can walk you through deploying the phone gateway within your call center or on an external cloud.'
  },
  contactCenterTitle: {
    id: 'phone.PhoneQuickSetup.contactCenterTitle',
    defaultMessage: 'Integrate with your contact center'
  },
  contactCenterDescription: {
    id: 'phone.PhoneQuickSetup.contactCenterDescription',
    defaultMessage: 'Connect Watson with your contact center.'
  },
  customDeploymentDescription: {
    id: 'phone.PhoneQuickSetup.customDeploymentDescription',
    defaultMessage:
      "Don't see what you need? <link>Contact us</link> for a custom deployment."
  },
  byoLabel: {
    id: 'phone.PhoneQuickSetup.byoLabel',
    defaultMessage: 'BYO SIP trunk'
  },
  setupGateway: {
    id: 'phone.PhoneQuickSetup.setupGateway',
    defaultMessage: 'Setup Gateway'
  },
  byoTitle: {
    id: 'phone.PhoneQuickSetup.byoTitle',
    defaultMessage: 'Bring your own SIP trunk'
  },
  byoDescriptionOne: {
    id: 'phone.PhoneQuickSetup.byoDescriptionOne',
    defaultMessage:
      'Watson Assistant already supports many SIP trunking providers, see the <link>supported list</link>. If your provider is not currently supported, submit a ticket to get yours added quickly.'
  },
  byoDescriptionTwo: {
    id: 'phone.PhoneQuickSetup.byoDescriptionTwo',
    defaultMessage: "To use your own SIP trunk, here's what you need to do:"
  },
  byoOrderedListOne: {
    id: 'phone.PhoneQuickSetup.byoOrderedListOne',
    defaultMessage:
      "Configure a SIP trunk with your provider - this is the virtual line that'll connect your assistant with a phone number. Either use your own telephony's provider, or use one of the following existing systems documented here:"
  },
  intelepeerOption: {
    id: 'phone.PhoneQuickSetup.intelepeerOption',
    defaultMessage: '<link>IntelePeer</link>'
  },
  genesysOption: {
    id: 'phone.PhoneQuickSetup.genesysOption',
    defaultMessage: 'Genesys'
  },
  niceOption: {
    id: 'phone.PhoneQuickSetup.niceOption',
    defaultMessage: 'NICE inContact'
  },
  twilioOption: {
    id: 'phone.PhoneQuickSetup.twilioOption',
    defaultMessage: '<link>Twilio</link>'
  },
  byoOption: {
    id: 'phone.PhoneQuickSetup.byoOption',
    defaultMessage: '<link>Bring your own</link>'
  },
  byoOrderedListTwo: {
    id: 'phone.PhoneQuickSetup.byoOrderedListTwo',
    defaultMessage:
      "Copy this SIP address and add it to your SIP trunking provider's origination configuration."
  },
  phoneNumberLabel: {
    id: 'phone.PhoneQuickSetup.phoneNumberLabel',
    defaultMessage: 'Phone number'
  },
  setupGatewayLabel: {
    id: 'phone.PhoneQuickSetup.setupGatewayLabel',
    defaultMessage: 'Setup Gateway'
  },
  phoneNumberDescription: {
    id: 'phone.PhoneQuickSetup.phoneNumberDescription',
    defaultMessage:
      'Create a primary phone number with your provider that your assistant will use to send and receive calls.'
  },
  selectContactCenterLabel: {
    id: 'phone.PhoneQuickSetup.selectContactCenterLabel',
    defaultMessage: 'Select contact center'
  },
  connectToContactCenterLabel: {
    id: 'phone.PhoneQuickSetup.connectToContactCenterLabel',
    defaultMessage: 'Connect to contact center'
  },
  ttsLabel: {
    id: 'phone.PhoneQuickSetup.ttsLabel',
    defaultMessage: 'Text to Speech'
  },
  sttLabel: {
    id: 'phone.PhoneQuickSetup.sttLabel',
    defaultMessage: 'Speech to Text'
  },
  next: {
    id: 'phone.PhoneQuickSetup.next',
    defaultMessage: 'Next'
  },
  finish: {
    id: 'phone.PhoneQuickSetup.finish',
    defaultMessage: 'Finish'
  },
  submit: {
    id: 'phone.PhoneQuickSetup.submit',
    defaultMessage: 'Submit'
  },
  cancel: {
    id: 'phone.PhoneQuickSetup.cancel',
    defaultMessage: 'Cancel'
  },
  back: {
    id: 'phone.PhoneQuickSetup.back',
    defaultMessage: 'Back'
  }
});
